import React, { useEffect, useState } from "react";
import styled from "styled-components";
import {
    RebelCard,
    RebelSectionTitle,
    RebelButton,
    RebelSecondaryButton,
    RebelDangerButton,
    RebelColors,
    RebelIcon,
    RebelEmptyState,
    RebelLoadingSpinner,
    RebelModal,
    RebelModalHeader,
    RebelModalBody,
    RebelModalFooter,
    RebelLabel,
    RebelInput,
    RebelTextarea
} from "../../../styles/rebel-experiences";
import { GetDB } from "../../../utils/database/firebase-budget-tool";
import { listenToCollection, setDocument, deleteDocument } from "../../../utils/firestore-functions";

const DEFAULT_CATEGORIES = [
    {
        id: 'meals',
        name: 'Meals',
        description: 'Food and dining experiences',
        order: 1,
        subcategories: ['Breakfast', 'Lunch', 'Dinner', 'Snack']
    },
    {
        id: 'morning-activity',
        name: 'Morning Activity',
        description: 'Activities scheduled for morning hours',
        order: 2,
        subcategories: ['Adventure', 'Wellness', 'Cultural', 'Nature', 'Sports']
    },
    {
        id: 'evening-activities',
        name: 'Evening Activities',
        description: 'Activities scheduled for evening hours',
        order: 3,
        subcategories: ['Entertainment', 'Social', 'Cultural', 'Recreation']
    },
    {
        id: 'business',
        name: 'Business',
        description: 'Professional and work-related activities',
        order: 4,
        subcategories: ['Workshop', 'Presentation', 'Meeting', 'Conference', 'Planning']
    },
    {
        id: 'free-time',
        name: 'Free Time',
        description: 'Unstructured time for personal activities',
        order: 5,
        subcategories: ['Relaxation', 'Arrival', 'Departure']
    },
    {
        id: 'team-building',
        name: 'Team Building',
        description: 'Group activities focused on team cohesion',
        order: 6,
        subcategories: ['Collaboration', 'Communication', 'Leadership', 'Problem-Solving']
    },
    {
        id: 'learning',
        name: 'Learning',
        description: 'Educational and skill-development activities',
        order: 7,
        subcategories: ['Workshop', 'Training', 'Seminar', 'Educational', 'Speaker']
    }
];

const RebelConfigList = () => {
    const [categories, setCategories] = useState([]);
    const [loading, setLoading] = useState(true);
    const [seeding, setSeeding] = useState(false);
    const [saving, setSaving] = useState(false);

    // Category modal state
    const [categoryModal, setCategoryModal] = useState(false);
    const [editingCategory, setEditingCategory] = useState(null);
    const [categoryForm, setCategoryForm] = useState({
        id: '',
        name: '',
        description: '',
        order: 0
    });

    // Delete category modal state
    const [deleteCategoryModal, setDeleteCategoryModal] = useState(false);
    const [categoryToDelete, setCategoryToDelete] = useState(null);

    // Subcategory modal state
    const [subcategoryModal, setSubcategoryModal] = useState(false);
    const [editingSubcategory, setEditingSubcategory] = useState(null);
    const [subcategoryForm, setSubcategoryForm] = useState({
        name: '',
        originalName: '',
        categoryId: ''
    });

    // Delete subcategory modal state
    const [deleteSubcategoryModal, setDeleteSubcategoryModal] = useState(false);
    const [subcategoryToDelete, setSubcategoryToDelete] = useState(null);

    // Confirm edit modal state (for breaking changes)
    const [confirmEditModal, setConfirmEditModal] = useState(false);
    const [pendingEdit, setPendingEdit] = useState(null);

    const budgetDB = GetDB();

    useEffect(() => {
        const unsubscribe = listenToCollection(budgetDB, "agenda-block-types", (data) => {
            if (!data || data.length === 0) {
                console.log("No categories found in 'agenda-block-types' collection. Data received:", data);
                setCategories([]);
            } else {
                console.log("Categories loaded from database:", data);
                const sortedCategories = data.sort((a, b) => {
                    if (a.order !== undefined && b.order !== undefined) {
                        return a.order - b.order;
                    }
                    return (a.name || '').localeCompare(b.name || '');
                });
                setCategories(sortedCategories);
            }
            setLoading(false);
        }, (error) => {
            console.error("Error fetching categories from 'agenda-block-types':", error);
            setLoading(false);
        });

        return () => {
            if (unsubscribe) unsubscribe();
        };
    }, [budgetDB]);

    const seedDefaultCategories = async () => {
        setSeeding(true);
        try {
            for (const category of DEFAULT_CATEGORIES) {
                await new Promise((resolve, reject) => {
                    setDocument(
                        budgetDB,
                        "agenda-block-types",
                        category.id,
                        {
                            ...category,
                            createdAt: new Date(),
                            updatedAt: new Date()
                        },
                        () => resolve(),
                        (error) => reject(error)
                    );
                });
            }
            console.log("Successfully seeded default categories to 'agenda-block-types'");
        } catch (error) {
            console.error("Error seeding categories:", error);
            alert("Error seeding categories. Check console for details.");
        } finally {
            setSeeding(false);
        }
    };

    // Category CRUD functions
    const openAddCategoryModal = () => {
        setCategoryForm({
            id: '',
            name: '',
            description: '',
            order: categories.length + 1
        });
        setEditingCategory(null);
        setCategoryModal(true);
    };

    const openEditCategoryModal = (category) => {
        setCategoryForm({
            id: category.id,
            name: category.name || '',
            description: category.description || '',
            order: category.order || 0
        });
        setEditingCategory(category);
        setCategoryModal(true);
    };

    const closeCategoryModal = () => {
        setCategoryModal(false);
        setEditingCategory(null);
        setCategoryForm({ id: '', name: '', description: '', order: 0 });
    };

    const handleCategoryFormChange = (field, value) => {
        setCategoryForm(prev => ({ ...prev, [field]: value }));
    };

    const saveCategory = async () => {
        // If editing, show confirmation modal
        if (editingCategory) {
            setPendingEdit({
                type: 'category',
                data: categoryForm,
                original: editingCategory
            });
            setCategoryModal(false);
            setConfirmEditModal(true);
            return;
        }

        // Adding new category
        await executeCategorySave();
    };

    const executeCategorySave = async () => {
        setSaving(true);
        try {
            const categoryId = editingCategory ? editingCategory.id : categoryForm.name.toLowerCase().replace(/\s+/g, '-');
            const existingCategory = editingCategory || categories.find(c => c.id === categoryId);

            const dataToSave = {
                id: categoryId,
                name: categoryForm.name,
                description: categoryForm.description,
                order: parseInt(categoryForm.order) || 0,
                subcategories: existingCategory?.subcategories || [],
                updatedAt: new Date()
            };

            if (!editingCategory) {
                dataToSave.createdAt = new Date();
            }

            await new Promise((resolve, reject) => {
                setDocument(
                    budgetDB,
                    "agenda-block-types",
                    categoryId,
                    dataToSave,
                    () => resolve(),
                    (error) => reject(error)
                );
            });

            closeCategoryModal();
            setConfirmEditModal(false);
            setPendingEdit(null);
        } catch (error) {
            console.error("Error saving category:", error);
            alert("Error saving category. Check console for details.");
        } finally {
            setSaving(false);
        }
    };

    const openDeleteCategoryModal = (category) => {
        setCategoryToDelete(category);
        setDeleteCategoryModal(true);
    };

    const handleDeleteCategory = async () => {
        if (!categoryToDelete) return;

        setSaving(true);
        try {
            await new Promise((resolve, reject) => {
                deleteDocument(
                    budgetDB,
                    "agenda-block-types",
                    categoryToDelete.id,
                    () => resolve(),
                    (error) => reject(error)
                );
            });
            setDeleteCategoryModal(false);
            setCategoryToDelete(null);
        } catch (error) {
            console.error("Error deleting category:", error);
            alert("Error deleting category. Check console for details.");
        } finally {
            setSaving(false);
        }
    };

    // Subcategory CRUD functions
    const openAddSubcategoryModal = (category) => {
        setSubcategoryForm({
            name: '',
            originalName: '',
            categoryId: category.id
        });
        setEditingSubcategory(null);
        setSubcategoryModal(true);
    };

    const openEditSubcategoryModal = (category, subcategoryName) => {
        setSubcategoryForm({
            name: subcategoryName,
            originalName: subcategoryName,
            categoryId: category.id
        });
        setEditingSubcategory({ category, name: subcategoryName });
        setSubcategoryModal(true);
    };

    const closeSubcategoryModal = () => {
        setSubcategoryModal(false);
        setEditingSubcategory(null);
        setSubcategoryForm({ name: '', originalName: '', categoryId: '' });
    };

    const saveSubcategory = async () => {
        // If editing, show confirmation modal
        if (editingSubcategory) {
            setPendingEdit({
                type: 'subcategory',
                data: subcategoryForm,
                original: editingSubcategory
            });
            setSubcategoryModal(false);
            setConfirmEditModal(true);
            return;
        }

        // Adding new subcategory
        await executeSubcategorySave();
    };

    const executeSubcategorySave = async () => {
        setSaving(true);
        try {
            const category = categories.find(c => c.id === subcategoryForm.categoryId);
            if (!category) throw new Error("Category not found");

            let updatedSubcategories = [...(category.subcategories || [])];

            if (editingSubcategory) {
                // Replace the old subcategory name with the new one
                const index = updatedSubcategories.indexOf(subcategoryForm.originalName);
                if (index > -1) {
                    updatedSubcategories[index] = subcategoryForm.name;
                }
            } else {
                // Add new subcategory
                updatedSubcategories.push(subcategoryForm.name);
            }

            await new Promise((resolve, reject) => {
                setDocument(
                    budgetDB,
                    "agenda-block-types",
                    category.id,
                    {
                        ...category,
                        subcategories: updatedSubcategories,
                        updatedAt: new Date()
                    },
                    () => resolve(),
                    (error) => reject(error)
                );
            });

            closeSubcategoryModal();
            setConfirmEditModal(false);
            setPendingEdit(null);
        } catch (error) {
            console.error("Error saving subcategory:", error);
            alert("Error saving subcategory. Check console for details.");
        } finally {
            setSaving(false);
        }
    };

    const openDeleteSubcategoryModal = (category, subcategoryName) => {
        setSubcategoryToDelete({ category, name: subcategoryName });
        setDeleteSubcategoryModal(true);
    };

    const handleDeleteSubcategory = async () => {
        if (!subcategoryToDelete) return;

        setSaving(true);
        try {
            const { category, name } = subcategoryToDelete;
            const updatedSubcategories = (category.subcategories || []).filter(s => s !== name);

            await new Promise((resolve, reject) => {
                setDocument(
                    budgetDB,
                    "agenda-block-types",
                    category.id,
                    {
                        ...category,
                        subcategories: updatedSubcategories,
                        updatedAt: new Date()
                    },
                    () => resolve(),
                    (error) => reject(error)
                );
            });

            setDeleteSubcategoryModal(false);
            setSubcategoryToDelete(null);
        } catch (error) {
            console.error("Error deleting subcategory:", error);
            alert("Error deleting subcategory. Check console for details.");
        } finally {
            setSaving(false);
        }
    };

    const confirmPendingEdit = async () => {
        if (!pendingEdit) return;

        if (pendingEdit.type === 'category') {
            await executeCategorySave();
        } else if (pendingEdit.type === 'subcategory') {
            await executeSubcategorySave();
        }
    };

    const cancelPendingEdit = () => {
        setConfirmEditModal(false);
        setPendingEdit(null);
        // Reopen the original modal
        if (pendingEdit?.type === 'category') {
            setCategoryModal(true);
        } else if (pendingEdit?.type === 'subcategory') {
            setSubcategoryModal(true);
        }
    };

    if (loading) {
        return (
            <RebelCard>
                <div style={{ textAlign: 'center', padding: '48px' }}>
                    <RebelLoadingSpinner />
                    <p style={{ marginTop: '16px', color: '#b0b0b0' }}>Loading Configuration...</p>
                </div>
            </RebelCard>
        );
    }

    return (
        <React.Fragment>
            <RebelCard>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' }}>
                    <RebelSectionTitle>
                        <RebelIcon icon={['fas', 'cogs']} />
                        Activity Categories
                    </RebelSectionTitle>
                    {categories.length > 0 && (
                        <RebelButton onClick={openAddCategoryModal}>
                            <RebelIcon icon={['fas', 'plus']} />
                            Add Category
                        </RebelButton>
                    )}
                </div>

                {categories.length === 0 ? (
                    <RebelEmptyState>
                        <RebelIcon icon={['fas', 'tags']} style={{ fontSize: '3rem', marginBottom: '16px' }} />
                        <h3>No Categories Found</h3>
                        <p>No activity categories were found in the database.</p>
                        <ButtonGroup>
                            <RebelButton onClick={seedDefaultCategories} disabled={seeding}>
                                <RebelIcon icon={['fas', 'plus']} />
                                {seeding ? 'Seeding...' : 'Seed Default Categories'}
                            </RebelButton>
                            <RebelSecondaryButton onClick={openAddCategoryModal}>
                                <RebelIcon icon={['fas', 'plus']} />
                                Add Custom Category
                            </RebelSecondaryButton>
                        </ButtonGroup>
                        <InfoText>
                            Seed defaults to quickly populate categories, or add them manually.
                        </InfoText>
                    </RebelEmptyState>
                ) : (
                    <CategoriesContainer>
                        {categories.map((category) => (
                            <CategoryCard key={category.id}>
                                <CategoryHeader>
                                    <CategoryName>
                                        <RebelIcon icon={['fas', 'tag']} />
                                        {category.name || category.id}
                                    </CategoryName>
                                    <CategoryActions>
                                        <CategoryBadge>
                                            {(category.subcategories || []).length} sub-categories
                                        </CategoryBadge>
                                        <ActionButton onClick={() => openEditCategoryModal(category)} title="Edit Category">
                                            <RebelIcon icon={['fas', 'edit']} />
                                        </ActionButton>
                                        <ActionButton onClick={() => openDeleteCategoryModal(category)} title="Delete Category" danger>
                                            <RebelIcon icon={['fas', 'trash']} />
                                        </ActionButton>
                                    </CategoryActions>
                                </CategoryHeader>

                                {category.description && (
                                    <CategoryDescription>{category.description}</CategoryDescription>
                                )}

                                <SubCategoriesContainer>
                                    <SubCategoriesHeader>
                                        <SubCategoriesTitle>Sub-Categories:</SubCategoriesTitle>
                                        <AddSubcategoryButton onClick={() => openAddSubcategoryModal(category)}>
                                            <RebelIcon icon={['fas', 'plus']} />
                                            Add
                                        </AddSubcategoryButton>
                                    </SubCategoriesHeader>
                                    {category.subcategories && category.subcategories.length > 0 ? (
                                        <SubCategoriesList>
                                            {category.subcategories.map((subCategory, index) => (
                                                <SubCategoryChip key={index}>
                                                    <span>{typeof subCategory === 'string' ? subCategory : subCategory.name || subCategory}</span>
                                                    <ChipActions>
                                                        <ChipButton onClick={() => openEditSubcategoryModal(category, subCategory)} title="Edit">
                                                            <RebelIcon icon={['fas', 'edit']} />
                                                        </ChipButton>
                                                        <ChipButton onClick={() => openDeleteSubcategoryModal(category, subCategory)} title="Delete" danger>
                                                            <RebelIcon icon={['fas', 'times']} />
                                                        </ChipButton>
                                                    </ChipActions>
                                                </SubCategoryChip>
                                            ))}
                                        </SubCategoriesList>
                                    ) : (
                                        <NoSubCategories>
                                            <RebelIcon icon={['fas', 'info-circle']} />
                                            No sub-categories defined
                                        </NoSubCategories>
                                    )}
                                </SubCategoriesContainer>
                            </CategoryCard>
                        ))}
                    </CategoriesContainer>
                )}
            </RebelCard>

            {/* Add/Edit Category Modal */}
            <RebelModal isOpen={categoryModal} toggle={closeCategoryModal}>
                <RebelModalHeader toggle={closeCategoryModal}>
                    {editingCategory ? 'Edit Category' : 'Add New Category'}
                </RebelModalHeader>
                <RebelModalBody>
                    <FormGroup>
                        <RebelLabel>Category Name *</RebelLabel>
                        <RebelInput
                            type="text"
                            value={categoryForm.name}
                            onChange={(e) => handleCategoryFormChange('name', e.target.value)}
                            placeholder="e.g., Team Building"
                        />
                    </FormGroup>
                    <FormGroup>
                        <RebelLabel>Description</RebelLabel>
                        <RebelTextarea
                            rows="3"
                            value={categoryForm.description}
                            onChange={(e) => handleCategoryFormChange('description', e.target.value)}
                            placeholder="Brief description of this category..."
                        />
                    </FormGroup>
                    <FormGroup>
                        <RebelLabel>Display Order</RebelLabel>
                        <RebelInput
                            type="number"
                            value={categoryForm.order}
                            onChange={(e) => handleCategoryFormChange('order', e.target.value)}
                            placeholder="1"
                            min="0"
                        />
                    </FormGroup>
                </RebelModalBody>
                <RebelModalFooter>
                    <RebelSecondaryButton onClick={closeCategoryModal}>
                        Cancel
                    </RebelSecondaryButton>
                    <RebelButton onClick={saveCategory} disabled={saving || !categoryForm.name.trim()}>
                        {saving ? 'Saving...' : editingCategory ? 'Save Changes' : 'Add Category'}
                    </RebelButton>
                </RebelModalFooter>
            </RebelModal>

            {/* Delete Category Confirmation Modal */}
            <RebelModal isOpen={deleteCategoryModal} toggle={() => setDeleteCategoryModal(false)}>
                <RebelModalHeader toggle={() => setDeleteCategoryModal(false)}>
                    Delete Category
                </RebelModalHeader>
                <RebelModalBody>
                    <WarningBox>
                        <RebelIcon icon={['fas', 'exclamation-circle']} />
                        <strong>Warning: This is a breaking change!</strong>
                    </WarningBox>
                    <p>Are you sure you want to delete the category:</p>
                    <HighlightText>{categoryToDelete?.name || categoryToDelete?.id}</HighlightText>
                    {categoryToDelete?.subcategories?.length > 0 && (
                        <p style={{ marginTop: '12px', color: RebelColors.warning }}>
                            This will also remove {categoryToDelete.subcategories.length} sub-categories.
                        </p>
                    )}
                    <p style={{ marginTop: '12px', fontSize: '13px', color: RebelColors.primaryMuted }}>
                        Existing activities using this category may be affected.
                    </p>
                </RebelModalBody>
                <RebelModalFooter>
                    <RebelSecondaryButton onClick={() => setDeleteCategoryModal(false)}>
                        Cancel
                    </RebelSecondaryButton>
                    <RebelDangerButton onClick={handleDeleteCategory} disabled={saving}>
                        <RebelIcon icon={['fas', 'trash']} />
                        {saving ? 'Deleting...' : 'Delete Category'}
                    </RebelDangerButton>
                </RebelModalFooter>
            </RebelModal>

            {/* Add/Edit Subcategory Modal */}
            <RebelModal isOpen={subcategoryModal} toggle={closeSubcategoryModal}>
                <RebelModalHeader toggle={closeSubcategoryModal}>
                    {editingSubcategory ? 'Edit Sub-Category' : 'Add New Sub-Category'}
                </RebelModalHeader>
                <RebelModalBody>
                    <FormGroup>
                        <RebelLabel>Sub-Category Name *</RebelLabel>
                        <RebelInput
                            type="text"
                            value={subcategoryForm.name}
                            onChange={(e) => setSubcategoryForm(prev => ({ ...prev, name: e.target.value }))}
                            placeholder="e.g., Workshop"
                        />
                    </FormGroup>
                    {editingSubcategory && (
                        <InfoBox>
                            <RebelIcon icon={['fas', 'info-circle']} />
                            Changing from: <strong>{subcategoryForm.originalName}</strong>
                        </InfoBox>
                    )}
                </RebelModalBody>
                <RebelModalFooter>
                    <RebelSecondaryButton onClick={closeSubcategoryModal}>
                        Cancel
                    </RebelSecondaryButton>
                    <RebelButton onClick={saveSubcategory} disabled={saving || !subcategoryForm.name.trim()}>
                        {saving ? 'Saving...' : editingSubcategory ? 'Save Changes' : 'Add Sub-Category'}
                    </RebelButton>
                </RebelModalFooter>
            </RebelModal>

            {/* Delete Subcategory Confirmation Modal */}
            <RebelModal isOpen={deleteSubcategoryModal} toggle={() => setDeleteSubcategoryModal(false)}>
                <RebelModalHeader toggle={() => setDeleteSubcategoryModal(false)}>
                    Delete Sub-Category
                </RebelModalHeader>
                <RebelModalBody>
                    <WarningBox>
                        <RebelIcon icon={['fas', 'exclamation-circle']} />
                        <strong>Warning: This is a breaking change!</strong>
                    </WarningBox>
                    <p>Are you sure you want to delete the sub-category:</p>
                    <HighlightText>{subcategoryToDelete?.name}</HighlightText>
                    <p style={{ marginTop: '12px', fontSize: '13px', color: RebelColors.primaryMuted }}>
                        From category: <strong>{subcategoryToDelete?.category?.name}</strong>
                    </p>
                    <p style={{ marginTop: '8px', fontSize: '13px', color: RebelColors.primaryMuted }}>
                        Existing activities using this sub-category may be affected.
                    </p>
                </RebelModalBody>
                <RebelModalFooter>
                    <RebelSecondaryButton onClick={() => setDeleteSubcategoryModal(false)}>
                        Cancel
                    </RebelSecondaryButton>
                    <RebelDangerButton onClick={handleDeleteSubcategory} disabled={saving}>
                        <RebelIcon icon={['fas', 'trash']} />
                        {saving ? 'Deleting...' : 'Delete Sub-Category'}
                    </RebelDangerButton>
                </RebelModalFooter>
            </RebelModal>

            {/* Confirm Edit Modal (for breaking changes) */}
            <RebelModal isOpen={confirmEditModal} toggle={cancelPendingEdit}>
                <RebelModalHeader toggle={cancelPendingEdit}>
                    Confirm Changes
                </RebelModalHeader>
                <RebelModalBody>
                    <WarningBox>
                        <RebelIcon icon={['fas', 'exclamation-circle']} />
                        <strong>Warning: This is a breaking change!</strong>
                    </WarningBox>
                    {pendingEdit?.type === 'category' && (
                        <>
                            <p>You are about to modify the category:</p>
                            <ChangesList>
                                {pendingEdit.original.name !== pendingEdit.data.name && (
                                    <ChangeItem>
                                        <span>Name:</span> {pendingEdit.original.name} <ArrowIcon icon={['fas', 'arrow-left']} /> {pendingEdit.data.name}
                                    </ChangeItem>
                                )}
                                {pendingEdit.original.description !== pendingEdit.data.description && (
                                    <ChangeItem>
                                        <span>Description:</span> Updated
                                    </ChangeItem>
                                )}
                                {pendingEdit.original.order !== parseInt(pendingEdit.data.order) && (
                                    <ChangeItem>
                                        <span>Order:</span> {pendingEdit.original.order} <ArrowIcon icon={['fas', 'arrow-left']} /> {pendingEdit.data.order}
                                    </ChangeItem>
                                )}
                            </ChangesList>
                        </>
                    )}
                    {pendingEdit?.type === 'subcategory' && (
                        <>
                            <p>You are about to rename the sub-category:</p>
                            <ChangesList>
                                <ChangeItem>
                                    {pendingEdit.data.originalName} <ArrowIcon icon={['fas', 'arrow-left']} /> {pendingEdit.data.name}
                                </ChangeItem>
                            </ChangesList>
                            <p style={{ fontSize: '13px', color: RebelColors.primaryMuted }}>
                                In category: <strong>{pendingEdit.original.category.name}</strong>
                            </p>
                        </>
                    )}
                    <p style={{ marginTop: '16px', fontSize: '13px', color: RebelColors.primaryMuted }}>
                        Existing activities using this {pendingEdit?.type} may be affected.
                    </p>
                </RebelModalBody>
                <RebelModalFooter>
                    <RebelSecondaryButton onClick={cancelPendingEdit}>
                        Go Back
                    </RebelSecondaryButton>
                    <RebelButton onClick={confirmPendingEdit} disabled={saving}>
                        {saving ? 'Saving...' : 'Confirm Changes'}
                    </RebelButton>
                </RebelModalFooter>
            </RebelModal>
        </React.Fragment>
    );
};

export default RebelConfigList;

const CategoriesContainer = styled.div`
    display: flex;
    flex-direction: column;
    gap: 16px;
`;

const CategoryCard = styled.div`
    background: ${RebelColors.background};
    border: 1px solid ${RebelColors.border};
    border-radius: 8px;
    padding: 20px;
    transition: all 0.3s ease;

    &:hover {
        border-color: ${RebelColors.gold}60;
        box-shadow: 0 2px 8px rgba(255, 215, 0, 0.1);
    }
`;

const CategoryHeader = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 12px;
    flex-wrap: wrap;
    gap: 12px;
`;

const CategoryName = styled.h4`
    color: ${RebelColors.primary};
    font-size: 16px;
    font-weight: 600;
    margin: 0;
    display: flex;
    align-items: center;
    gap: 8px;

    svg {
        color: ${RebelColors.gold};
    }
`;

const CategoryActions = styled.div`
    display: flex;
    align-items: center;
    gap: 8px;
`;

const CategoryBadge = styled.span`
    background: ${RebelColors.gold}20;
    color: ${RebelColors.gold};
    padding: 4px 12px;
    border-radius: 20px;
    font-size: 12px;
    font-weight: 500;
`;

const ActionButton = styled.button`
    background: ${props => props.danger ? `${RebelColors.danger}20` : `${RebelColors.surfaceLight}`};
    border: 1px solid ${props => props.danger ? RebelColors.danger : RebelColors.border};
    color: ${props => props.danger ? RebelColors.danger : RebelColors.primary};
    border-radius: 6px;
    padding: 6px 10px;
    cursor: pointer;
    transition: all 0.2s ease;
    display: flex;
    align-items: center;
    justify-content: center;

    svg {
        margin: 0;
        font-size: 12px;
        color: inherit;
    }

    &:hover {
        background: ${props => props.danger ? RebelColors.danger : RebelColors.gold};
        color: ${props => props.danger ? '#fff' : '#000'};
        border-color: ${props => props.danger ? RebelColors.danger : RebelColors.gold};
    }
`;

const CategoryDescription = styled.p`
    color: ${RebelColors.primaryMuted};
    font-size: 14px;
    margin: 0 0 16px 0;
    line-height: 1.5;
`;

const SubCategoriesContainer = styled.div`
    margin-top: 12px;
    padding-top: 12px;
    border-top: 1px solid ${RebelColors.border};
`;

const SubCategoriesHeader = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 12px;
`;

const SubCategoriesTitle = styled.p`
    color: ${RebelColors.primaryMuted};
    font-size: 12px;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.5px;
    margin: 0;
`;

const AddSubcategoryButton = styled.button`
    background: transparent;
    border: 1px dashed ${RebelColors.border};
    color: ${RebelColors.primaryMuted};
    border-radius: 4px;
    padding: 4px 10px;
    font-size: 12px;
    cursor: pointer;
    transition: all 0.2s ease;
    display: flex;
    align-items: center;
    gap: 4px;

    svg {
        margin: 0;
        font-size: 10px;
    }

    &:hover {
        border-color: ${RebelColors.gold};
        color: ${RebelColors.gold};
        background: ${RebelColors.gold}10;
    }
`;

const SubCategoriesList = styled.div`
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
`;

const SubCategoryChip = styled.div`
    background: ${RebelColors.surfaceLight};
    color: ${RebelColors.primary};
    padding: 6px 12px;
    border-radius: 16px;
    font-size: 13px;
    border: 1px solid ${RebelColors.border};
    transition: all 0.2s ease;
    display: flex;
    align-items: center;
    gap: 8px;

    &:hover {
        background: ${RebelColors.surface};
        border-color: ${RebelColors.gold}40;
    }
`;

const ChipActions = styled.div`
    display: flex;
    gap: 4px;
    margin-left: 4px;
`;

const ChipButton = styled.button`
    background: transparent;
    border: none;
    color: ${props => props.danger ? RebelColors.danger : RebelColors.primaryMuted};
    padding: 2px 4px;
    cursor: pointer;
    border-radius: 4px;
    transition: all 0.2s ease;
    display: flex;
    align-items: center;

    svg {
        margin: 0;
        font-size: 10px;
    }

    &:hover {
        background: ${props => props.danger ? `${RebelColors.danger}20` : `${RebelColors.gold}20`};
        color: ${props => props.danger ? RebelColors.danger : RebelColors.gold};
    }
`;

const NoSubCategories = styled.div`
    color: ${RebelColors.primaryMuted};
    font-size: 13px;
    font-style: italic;
    display: flex;
    align-items: center;
    gap: 8px;

    svg {
        font-size: 12px;
    }
`;

const InfoText = styled.p`
    color: ${RebelColors.primaryMuted};
    font-size: 13px;
    margin-top: 16px;

    code {
        background: ${RebelColors.surfaceLight};
        padding: 2px 6px;
        border-radius: 4px;
        font-family: monospace;
        color: ${RebelColors.gold};
    }
`;

const ButtonGroup = styled.div`
    display: flex;
    gap: 12px;
    flex-wrap: wrap;
    justify-content: center;
`;

const FormGroup = styled.div`
    margin-bottom: 16px;

    label {
        display: block;
        margin-bottom: 8px;
    }

    input, textarea {
        width: 100%;
    }
`;

const WarningBox = styled.div`
    background: ${RebelColors.warning}15;
    border: 1px solid ${RebelColors.warning};
    border-radius: 8px;
    padding: 12px 16px;
    margin-bottom: 16px;
    display: flex;
    align-items: center;
    gap: 10px;
    color: ${RebelColors.warning};

    svg {
        font-size: 18px;
        color: ${RebelColors.warning};
    }
`;

const InfoBox = styled.div`
    background: ${RebelColors.accent}15;
    border: 1px solid ${RebelColors.accent};
    border-radius: 8px;
    padding: 12px 16px;
    margin-top: 12px;
    display: flex;
    align-items: center;
    gap: 10px;
    color: ${RebelColors.accent};
    font-size: 13px;

    svg {
        font-size: 14px;
        color: ${RebelColors.accent};
    }

    strong {
        color: ${RebelColors.primary};
    }
`;

const HighlightText = styled.p`
    background: ${RebelColors.surfaceLight};
    padding: 10px 16px;
    border-radius: 6px;
    color: ${RebelColors.gold};
    font-weight: 600;
    margin: 12px 0;
`;

const ChangesList = styled.div`
    background: ${RebelColors.surfaceLight};
    border-radius: 6px;
    padding: 12px 16px;
    margin: 12px 0;
`;

const ChangeItem = styled.div`
    color: ${RebelColors.primary};
    font-size: 13px;
    padding: 6px 0;
    display: flex;
    align-items: center;
    gap: 8px;

    span {
        color: ${RebelColors.primaryMuted};
        min-width: 80px;
    }

    & + & {
        border-top: 1px solid ${RebelColors.border};
    }
`;

const ArrowIcon = styled(RebelIcon)`
    color: ${RebelColors.gold};
    font-size: 10px;
    transform: rotate(180deg);
`;
